import React, { useState } from 'react';
import styled from 'astroturf/react';
import PremiumBadge from './PremiumBadge';

import { Aedu } from 'global';
import {
  Avatar,
  Card,
  Header,
  Institution,
  RoundButton,
  ViewAll,
} from './common';
import { TruncateText as TruncateTextBase } from 'js/liswp/TruncateText';
import pluralize from 'pluralize';
import { Author } from './hooks/types';

type Props = {
  // Whether to use the alternate clean look (part of swp_clean_rr ab test)
  altLook?: boolean;
  authors: Author[];
  onFollow: (author: Author) => void;
  onMessage: (author: Author) => void;
};

type SingleProps = {
  altLook?: boolean;
  author: Author;
  onFollow: (author: Author) => void;
  onMessage: (author: Author) => void;
};

const INITIAL_LIMIT = 3;
const RIGHT_ARROW = '\u25b8';
const PLACEHOLDER_PHOTO = '//c.academia-assets.com/images/s65_no_pic.png';

const AuthorBody = styled.div<{ altLook?: boolean }>`
  border-bottom: 1px solid #ddd;
  padding: 12px;

  &.altLook {
    border-bottom: 0;
  }
`;

const AvatarGroup = styled.div`
  display: flex;
`;

const BottomGroup = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const Button = styled.button`
  background: transparent;
  border: 0;
  color: #0954d3;
  font-size: 13px;
  margin-right: 4px;
  padding: 0 4px;
`;

const ButtonGroup = styled.div`
  align-items: center;
  display: flex;
`;

const Check = styled('i').attrs<{ margin?: boolean }>({
  className: 'fa fa-check',
})`
  &.margin {
    margin-right: 6px;
  }
`;

const Comment = styled('i').attrs<{ margin?: boolean }>({
  className: 'fa fa-comment',
})`
  &.margin {
    margin-right: 6px;
  }
`;

const Name = styled.a`
  color: #333;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  margin: 0 8px 0 0;

  &:hover {
    color: #333;
    text-decoration: underline;
  }
`;

const NameGroup = styled.div<{ altLook?: boolean }>`
  display: flex;

  &.altLook {
    align-items: center;
  }

  > * {
    margin-right: 7px;
  }
`;

const Plus = styled('i').attrs<{ altLook?: boolean }>({
  className: 'fa fa-plus',
})`
  margin-right: 6px;

  &.altLook {
    font-size: 12px;
    margin-right: 10px;
  }
`;

const StatContainer = styled.div`
  padding-right: 12px;

  &:not(:last-of-type) {
    border-right: 1px solid #ddd;
    margin-right: 12px;
  }
`;

const StatTitle = styled.h3<{ altLook?: boolean }>`
  color: #777;
  font-size: 10px;
  letter-spacing: 0.5px;
  line-height: 1em;
  margin: 0 0 3px;
  text-transform: uppercase;

  &.altLook {
    color: #888;
    font-size: 11px;
    font-weight: 400;
  }
`;

const StatValue = styled.p<{ altLook?: boolean }>`
  color: #333;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;

  &.altLook {
    font-size: 15px;
  }
`;

const StatsGroup = styled.div`
  display: flex;
`;

const TruncateText = styled(TruncateTextBase)`
  margin-top: 16px;
`;

const CircleButton = styled(RoundButton)`
  border-radius: 50%;
  height: 36px;
  padding: 0;
  width: 36px;
`;

const formatter = new Intl.NumberFormat();
const format = (num: number) => formatter.format(num);

const Stat = ({
  altLook,
  title,
  value,
}: {
  altLook?: boolean;
  title: string;
  value: number;
}) => (
  <StatContainer>
    <StatTitle altLook={altLook}>{title}</StatTitle>
    <StatValue altLook={altLook}>{format(value)}</StatValue>
  </StatContainer>
);

// Used when a work only has one author
// The card is slightly different looking and shows more information
// than if there are multiple authors
const SingleAuthor = ({
  altLook,
  author,
  onFollow,
  onMessage,
}: SingleProps) => {
  const isUserAuthor = Aedu.User?.current?.id === author.id;

  return (
    <AuthorBody altLook={altLook}>
      <AvatarGroup>
        <Avatar altLook={altLook} src={author.photo ?? PLACEHOLDER_PHOTO} />

        <div>
          <NameGroup altLook={altLook}>
            <Name href={author.url}>{author.displayName}</Name>
            {author.show_premium_badge && <PremiumBadge />}
          </NameGroup>
          <Institution altLook={altLook}>{author.university}</Institution>
          <Institution altLook={altLook}>{author.position}</Institution>
        </div>
      </AvatarGroup>

      {author.bio && <TruncateText text={author.bio} characters={250} />}

      <BottomGroup>
        <StatsGroup>
          {author.papers != null && (
            <Stat altLook={altLook} title="Papers" value={author.papers} />
          )}
          {author.followers != null && (
            <Stat
              altLook={altLook}
              title="Followers"
              value={author.followers}
            />
          )}
        </StatsGroup>

        {!isUserAuthor && (
          <ButtonGroup>
            <RoundButton
              gray={author.following}
              onClick={() => onFollow(author)}
            >
              <Plus altLook={altLook} />
              {author.following ? 'Following' : 'Follow'}
            </RoundButton>
            <CircleButton onClick={() => onMessage(author)}>
              <Comment />
            </CircleButton>
          </ButtonGroup>
        )}
      </BottomGroup>
    </AuthorBody>
  );
};

// Used when a work has multiple authors
// The card shows a little less information per author than if
// there was only one author
const MultiAuthor = ({ altLook, author, onFollow, onMessage }: SingleProps) => {
  const isUserAuthor = Aedu.User?.current.id === author.id;

  return (
    <AuthorBody altLook={altLook}>
      <AvatarGroup>
        <Avatar altLook={altLook} src={author.photo ?? PLACEHOLDER_PHOTO} />

        <div>
          <NameGroup>
            <Name href={author.url}>{author.displayName}</Name>
            {author.show_premium_badge && <PremiumBadge />}
          </NameGroup>
          <Institution>
            {[author.position, author.university].filter(Boolean).join(', ')}
          </Institution>
          {!isUserAuthor && (
            <ButtonGroup>
              <Button onClick={() => onFollow(author)}>
                {author.following ? <Check margin /> : <Plus />}
                Follow
              </Button>
              {/* On prod, this button has a font-size 1px smaller than
               * the previous button, not sure if it's a mistake.
               */}
              <Button onClick={() => onMessage(author)}>
                <Comment margin />
                Message
              </Button>
            </ButtonGroup>
          )}
        </div>
      </AvatarGroup>
    </AuthorBody>
  );
};

// This component is for when a work has multiple authors
// For when a paper has only one author, see AuthorCard
const AuthorsCard: React.FC<Props> = ({
  altLook,
  authors,
  onFollow,
  onMessage,
}: Props) => {
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  const showViewAll = limit < authors.length;
  const limited = authors.slice(0, limit);

  return (
    <Card altLook={altLook}>
      <Header altLook={altLook}>
        {`About ${pluralize('Author', authors.length)}`}
      </Header>

      {authors.length === 1 && (
        <SingleAuthor
          altLook={altLook}
          author={authors[0]}
          onFollow={onFollow}
          onMessage={onMessage}
        />
      )}

      {authors.length > 1 &&
        limited.map((author, index) => (
          <MultiAuthor
            altLook={altLook}
            author={author}
            onFollow={onFollow}
            onMessage={onMessage}
            key={index}
          />
        ))}

      {showViewAll && (
        <ViewAll onClick={() => setLimit(Infinity)}>
          View All {RIGHT_ARROW}
        </ViewAll>
      )}
    </Card>
  );
};

export default AuthorsCard;
